import React from 'react'
import { Link } from '@tanstack/react-router'
import { ThaiWordFullProps } from 'core/interfaces/dictionary-thai-interfaces'

import { playSound, ThaiVoiceGender } from '../../../lib/sounds'
import SmallLogo from '../../basic/small-logo'
import { SentenceWithTranslit } from '../../../lib/interfaces'
import ErrorOccurred from '../../errors/error-occurred'
import ExampleSentencesList from '../example-sentences-list'
import NotFoundMessage from 'components/basic/not-found-message'

// Static imports: both are in the server HTML, and lazy() would keep their
// stylesheets out of the initial <head> (see main-layout.tsx).
import MainWordBar from './main-word-bar'
import ThaiWordMeanings from './thai-word-meanings'
import { PartHoverProvider } from './part-hover-context'
import { groupThaiMeanings } from '../../../lib/sense-groups'
import { distinctReadings } from '../../../lib/pronunciation'

type ErrorType = 'Word Does Not Exist' | 'Server Error'

export type ThaiDictionaryProps = {
   errorType?: ErrorType
   dictionaryWord: ThaiWordFullProps
   // Word-level fallback: sentences not yet classified to a sense.
   exampleSentences: SentenceWithTranslit[]
   exampleSentencesByMeaningId: Record<number, SentenceWithTranslit[]>
}

const playThisSound = (dictionaryWord: ThaiWordFullProps, gender: ThaiVoiceGender) =>
   playSound(dictionaryWord.word, gender)

const ThaiDictionaryPage = (props: ThaiDictionaryProps) => {
   const { errorType, dictionaryWord } = props

   if (errorType && errorType === 'Word Does Not Exist') {
      return <NotFoundMessage>Dictionary entry does not exist</NotFoundMessage>
   }

   if (errorType && errorType === 'Server Error') {
      return <ErrorOccurred />
   }

   // Linked senses (oil ↔ oily) render as one merged block: the group's primary
   // as today, siblings as compact sub-lines. The worker pools the group's
   // compounds and classified sentences onto the primary's id.
   const senseGroups = groupThaiMeanings(dictionaryWord.meanings)
   const groupPrimaries = senseGroups.map((group) => group.primary)

   // Heteronym readings for the hero, from every sense rather than the group
   // primaries — a sense that reads differently need not be a group's primary.
   const heteronymReadings = distinctReadings(dictionaryWord.t2e, dictionaryWord.meanings).slice(1)

   // Compound "assembly" breakdown: the primary meaning's constituent parts,
   // rendered under the first sense (see ThaiWordMeanings). Those parts are
   // dropped from that sense's related grid to avoid listing them twice.
   const breakdownParts = groupPrimaries[0]?.components ?? []
   const showBreakdown = breakdownParts.length >= 2

   return (
      <PartHoverProvider>
         <div className="flex justify-center px-[8px] sm:px-[20px] pt-[8px] pb-[40px] bg-pageBg">
            <div className="w-[900px] max-w-full">
               <MainWordBar
                  word={dictionaryWord.word}
                  parts={showBreakdown ? breakdownParts.map((part) => part.word) : undefined}
                  meanings={groupPrimaries}
                  separable={dictionaryWord.separable}
                  phoneticSpaced={dictionaryWord.t2e}
                  phoneticAlternates={heteronymReadings}
                  commonnessScore={dictionaryWord.commonnessScore}
                  commonnessBlurb={dictionaryWord.commonnessBlurb}
                  hasSoundFile={dictionaryWord.hasSoundFile}
                  audioGenders={dictionaryWord.audioGenders}
                  playSound={(gender) => playThisSound(dictionaryWord, gender)}
               />

               {dictionaryWord.word === 'คาราโอเกะ' && (
                  <div className="bg-rowOdd border border-hairline rounded-[16px] p-[20px_40px] max-w-[600px] text-center mt-[40px] mx-auto [&_a]:text-bodyBlack [&_b]:text-[24px] [&_b]:inline-block [&_b]:mr-[5px]">
                     <SmallLogo />
                     <Link to="/ภาษาคาราโอเกะ">
                        <b>→</b> แปลภาษาไทยเป็นคาราโอเกะโดยอัตโนมัติ
                     </Link>
                  </div>
               )}

               <ul id="english-word-thai-meanings" className="mt-[28px] mb-0 mx-0 p-0 list-none">
                  {senseGroups.map((group, index) => (
                     <ThaiWordMeanings
                        key={index}
                        meaning={group.primary}
                        siblingMeanings={group.siblings}
                        index={index}
                        exampleSentences={props.exampleSentencesByMeaningId[group.primary.id] ?? []}
                        headword={dictionaryWord.word}
                        showNumber={senseGroups.length > 1}
                        hideComponents={showBreakdown && index === 0}
                        breakdownParts={showBreakdown && index === 0 ? breakdownParts : undefined}
                     />
                  ))}
               </ul>

               <ExampleSentencesList
                  exampleSentences={props.exampleSentences}
                  headword={dictionaryWord.word}
                  title={
                     Object.keys(props.exampleSentencesByMeaningId).length > 0
                        ? 'More real sentences'
                        : undefined
                  }
               />
            </div>
         </div>
      </PartHoverProvider>
   )
}

export default ThaiDictionaryPage
